import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";

const monthLabel = (value) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const index = Number(value) - 1;
  return months[index] || "N/A";
};

export default function OffseasonSkillGeneratorConfirm({ userId }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const programId = searchParams.get("programId");
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState(programId || "");
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPrograms = async () => {
      if (!userId) return;
      const { data, error } = await supabase
        .from("user_program_follows")
        .select("program_id, programs(name, sport)")
        .eq("user_id", userId)
        .order("order_index", { ascending: true });

      if (!error) {
        setPrograms(data || []);
        if (!selectedProgramId && data?.length > 0) {
          setSelectedProgramId(data[0].program_id);
        }
      }
    };

    fetchPrograms();
  }, [userId, selectedProgramId]);

  useEffect(() => {
    const fetchProgram = async () => {
      if (!selectedProgramId) return;
      const { data, error } = await supabase
        .from("programs")
        .select("id, name, sport, off_season_start_month, off_season_end_month")
        .eq("id", selectedProgramId)
        .maybeSingle();

      if (!error) setProgram(data || null);
    };

    fetchProgram();
  }, [selectedProgramId]);

  const handleCheckout = async () => {
    if (!selectedProgramId) return;
    setLoading(true);

    const productId = "prod_U7yLraOrUlKZFu";
    const successUrl = `${window.location.origin}/skills-workouts?status=success`;
    const cancelUrl = `${window.location.origin}/athlete/offseason-skill-generator?programId=${selectedProgramId}&status=cancel`;

    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;

      const { data, error } = await supabase.functions.invoke(
        "athlete-skill-generator-checkout",
        {
          body: {
            productId,
            programId: selectedProgramId,
            athleteId: user?.id || userId,
            customerEmail: user?.email,
            successUrl,
            cancelUrl,
          },
        }
      );

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Skill generator checkout error:", err);
      alert(err.message || "Unable to start checkout.");
    } finally {
      setLoading(false);
    }
  };

  const offseasonLabel = program
    ? `${monthLabel(program.off_season_start_month)} - ${monthLabel(program.off_season_end_month)}`
    : "offseason window";

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h1 className="text-xl font-bold text-slate-900">Offseason Skill Generator</h1>
        <p className="text-sm text-slate-500 mt-2">
          Get offseason skill workouts tied to your selected program.
        </p>

        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-500 uppercase font-semibold">Program</p>
          <select
            value={selectedProgramId}
            onChange={(e) => setSelectedProgramId(e.target.value)}
            className="mt-2 w-full bg-white border border-slate-200 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
          >
            {programs.map((p) => (
              <option key={p.program_id} value={p.program_id}>
                {p.programs?.name || "Program"}
              </option>
            ))}
          </select>
          <p className="text-base font-semibold text-slate-900 mt-3">{program?.name || "Program"}</p>
          {program?.sport && <p className="text-xs text-slate-500">{program.sport}</p>}
          <p className="text-xs text-slate-500 mt-2">Offseason window: {offseasonLabel}</p>
        </div>

        <p className="text-xs text-slate-500 mt-4">
          Free workouts are available during the offseason window for this program.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? "Starting checkout..." : "Buy Offseason Skill Generator"}
          </button>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-bold rounded-lg hover:bg-slate-200 transition"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
}
